import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FileText,
  UploadCloud,
  DownloadCloud,
  XCircle,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Trash2,
  ExternalLink,
  FileSpreadsheet,
  Type,
  Info,
  ChevronDown,
  ChevronUp,
  Copy,
  Hash,
  ScanLine,
} from "lucide-react";
import { API } from "../utils/apiUrls";

// ────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────
const getAuth = () => {
  const Authorization = localStorage.getItem("authToken");
  const merchantId = localStorage.getItem("merchantId");
  if (!Authorization || !merchantId) throw new Error("Not authenticated");
  return { Authorization, merchantId };
};

/**
 * Parse CSV/XLSX-style text content and extract AWB or Order Reference numbers.
 * Looks for columns named: awb_number, awb, order_reference_number, order_ref, order_reference
 * Falls back to first column if no header matches.
 */
const parseFileContent = (text) => {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return { awbNumbers: [], orderRefNumbers: [], mode: null };

  // Try to detect separator (comma or tab)
  const separator = lines[0].includes("\t") ? "\t" : ",";
  const headers = lines[0].toLowerCase().split(separator).map((h) => h.trim().replace(/^["']|["']$/g, ""));

  const awbIdx = headers.findIndex((h) =>
    ["awb_number", "awb", "awb_no", "awbnumber"].includes(h)
  );
  const orderRefIdx = headers.findIndex((h) =>
    ["order_reference_number", "order_ref", "order_reference", "orderref", "order_ref_number"].includes(h)
  );

  const hasKnownHeader = awbIdx !== -1 || orderRefIdx !== -1;
  const dataLines = hasKnownHeader ? lines.slice(1) : lines;

  const awbNumbers = [];
  const orderRefNumbers = [];

  for (const line of dataLines) {
    const cols = line.split(separator).map((c) => c.trim().replace(/^["']|["']$/g, ""));
    if (awbIdx !== -1 && cols[awbIdx]) {
      awbNumbers.push(cols[awbIdx]);
    }
    if (orderRefIdx !== -1 && cols[orderRefIdx]) {
      orderRefNumbers.push(cols[orderRefIdx]);
    }
    // If no known header, treat first column as AWB
    if (!hasKnownHeader && cols[0]) {
      awbNumbers.push(cols[0]);
    }
  }

  const mode = orderRefNumbers.length > 0 && awbNumbers.length === 0
    ? "order_ref"
    : "awb";

  return { awbNumbers, orderRefNumbers, mode };
};

/**
 * Parse the manual text input: comma, newline, or space-separated values.
 */
const parseManualInput = (text) => {
  return text
    .split(/[,\n\r]+/)
    .map((v) => v.trim())
    .filter(Boolean);
};

// ────────────────────────────────────────────
// Component
// ────────────────────────────────────────────
const BulkInvoiceDownload = () => {
  // Input mode: "file" or "manual"
  const [inputMode, setInputMode] = useState("file");

  // File upload state
  const [file, setFile] = useState(null);
  const [fileParsed, setFileParsed] = useState(null); // { awbNumbers, orderRefNumbers, mode }
  const fileInputRef = useRef(null);

  // Manual input state
  const [manualText, setManualText] = useState("");
  const [manualMode, setManualMode] = useState("awb"); // "awb" or "order_ref"

  // Processing state
  const [loading, setLoading] = useState(false);

  // Results state
  const [results, setResults] = useState(null);
  // {
  //   mergedUrl, expiresAt,
  //   summary: { total, success_count, failed_count },
  //   items: [{ order_reference_number, awb_number, success, url, expires, error_message }]
  // }

  const [showAllResults, setShowAllResults] = useState(false);

  // ──────────────────────────
  // File handling
  // ──────────────────────────
  const handleFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    // Validate file type
    const validTypes = [".csv", ".xlsx", ".xls", ".txt"];
    const ext = f.name.substring(f.name.lastIndexOf(".")).toLowerCase();
    if (!validTypes.includes(ext)) {
      toast.error("Please upload a CSV, XLSX, or TXT file");
      return;
    }

    // Validate file size (5MB max)
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File size must be under 5 MB");
      return;
    }

    setFile(f);
    setResults(null);

    // Parse file contents
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const parsed = parseFileContent(text);
      setFileParsed(parsed);

      const totalEntries = parsed.awbNumbers.length + parsed.orderRefNumbers.length;
      if (totalEntries === 0) {
        toast.warning("No AWB or Order Reference numbers found in the file. Please check the format.");
      } else {
        toast.success(`Found ${totalEntries} entries in the file`);
      }
    };
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsText(f);
  }, []);

  const clearFile = useCallback(() => {
    setFile(null);
    setFileParsed(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  // ──────────────────────────
  // Sample CSV download
  // ──────────────────────────
  const handleSampleDownload = useCallback(() => {
    const csvContent = "awb_number,order_reference_number\n50845010161663,ORD-001\n50845010161664,ORD-002\n50845010161665,ORD-003\n";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk_invoice_download_sample.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  // ──────────────────────────
  // Build the final numbers list
  // ──────────────────────────
  const getPayloadData = useCallback(() => {
    if (inputMode === "file") {
      if (!fileParsed) return null;
      const { awbNumbers, orderRefNumbers, mode } = fileParsed;
      if (mode === "order_ref" && orderRefNumbers.length > 0) {
        return { order_reference_numbers: orderRefNumbers };
      }
      if (awbNumbers.length > 0) {
        return { awb_numbers: awbNumbers };
      }
      return null;
    }

    // manual mode
    const values = parseManualInput(manualText);
    if (values.length === 0) return null;

    if (manualMode === "order_ref") {
      return { order_reference_numbers: values };
    }
    return { awb_numbers: values };
  }, [inputMode, fileParsed, manualText, manualMode]);

  const entryCount = (() => {
    const data = getPayloadData();
    if (!data) return 0;
    return (data.awb_numbers || data.order_reference_numbers || []).length;
  })();

  // ──────────────────────────
  // API call
  // ──────────────────────────
  const handleDownload = useCallback(async () => {
    const payloadData = getPayloadData();
    if (!payloadData) {
      toast.error(
        inputMode === "file"
          ? "Please upload a file with valid AWB or Order Reference numbers"
          : "Please enter at least one AWB or Order Reference number"
      );
      return;
    }

    const count = (payloadData.awb_numbers || payloadData.order_reference_numbers).length;
    if (count > 500) {
      toast.error("Maximum 500 entries allowed per request. Please reduce the number of entries.");
      return;
    }

    try {
      setLoading(true);
      setResults(null);

      const { Authorization, merchantId } = getAuth();
      const url = API.bulkShipmentInvoice();

      const body = {
        merchant_id: merchantId,
        ...payloadData,
      };

      const { data } = await axios.post(url, body, {
        headers: {
          Authorization,
          "Content-Type": "application/json",
          "x-merchant-id": merchantId,
        },
        timeout: 5 * 60 * 1000, // 5 min for bulk
      });

      // Build results state
      const resultData = {
        mergedUrl: data.merged_shipment_invoice_presigned_url || null,
        expiresAt: data.merged_shipment_invoice_expiration_date_time_utc || null,
        summary: data.summary || { total: 0, success_count: 0, failed_count: 0 },
        items: Array.isArray(data.results) ? data.results.map((r) => ({
          order_reference_number: r.order_reference_number || "-",
          awb_number: r.awb_number || "-",
          success: r.success,
          url: r.shipment_invoice_presigned_url || null,
          expires: r.shipment_invoice_expiration_date_time_utc || null,
          error_message: r.error_message || r.failure_reason || null,
        })) : [],
      };

      setResults(resultData);

      // Auto-open merged PDF in new tab
      if (resultData.mergedUrl) {
        window.open(resultData.mergedUrl, "_blank", "noopener,noreferrer");
        toast.success(`Invoices generated! ${resultData.summary.success_count} of ${resultData.summary.total} succeeded.`);
      } else if (resultData.summary.success_count > 0) {
        toast.success(`${resultData.summary.success_count} invoices generated successfully.`);
      } else {
        toast.error("No invoices could be generated. Check the results below.");
      }
    } catch (err) {
      console.error("Bulk invoice download failed:", err);
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error_message ||
        err?.message ||
        "Failed to download invoices. Please try again.";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [getPayloadData, inputMode]);

  // ──────────────────────────
  // Copy URL to clipboard
  // ──────────────────────────
  const copyUrl = useCallback((url) => {
    navigator.clipboard.writeText(url).then(
      () => toast.success("URL copied to clipboard"),
      () => toast.error("Failed to copy URL")
    );
  }, []);

  // ──────────────────────────
  // Results display helpers
  // ──────────────────────────
  const displayedResults = showAllResults
    ? results?.items || []
    : (results?.items || []).slice(0, 10);

  const failedItems = (results?.items || []).filter((r) => !r.success);
  const successItems = (results?.items || []).filter((r) => r.success);

  // ────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────
  return (
    <div className="w-full min-h-[calc(100vh-88px)] bg-slate-50 px-2">
      <div className="w-full h-full pl-0 pr-3 sm:pr-4 md:pr-6 lg:pr-8 pt-2 md:pt-2 pb-4 space-y-4 px-2">

        {/* ───── Header ───── */}
        <div className="flex flex-col gap-1 min-w-0">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Bulk Invoice Download
          </h3>
          <p className="text-[0.7rem] md:text-xs text-slate-600 max-w-3xl">
            Generate and download shipment invoices in bulk by uploading a CSV file or entering AWB/Order Reference numbers manually.
            The merged invoice PDF will open automatically in a new tab.
          </p>
        </div>

        {/* ───── Input Mode Tabs ───── */}
        <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          {/* Tab header */}
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => { setInputMode("file"); setResults(null); }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all border-b-2 ${
                inputMode === "file"
                  ? "border-blue-600 text-blue-600 bg-blue-50/50"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <FileSpreadsheet className="w-4 h-4" />
              Upload File
            </button>
            <button
              onClick={() => { setInputMode("manual"); setResults(null); }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-all border-b-2 ${
                inputMode === "manual"
                  ? "border-blue-600 text-blue-600 bg-blue-50/50"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
              }`}
            >
              <Type className="w-4 h-4" />
              Enter Manually
            </button>
          </div>

          {/* Tab content */}
          <div className="p-4 sm:p-5">

            {/* ─── FILE UPLOAD TAB ─── */}
            {inputMode === "file" && (
              <div className="space-y-4">
                {/* Drop zone */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="group relative flex flex-col items-center justify-center gap-3 p-8 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/50 hover:border-blue-400 hover:bg-blue-50/30 transition-all cursor-pointer"
                >
                  <div className="h-12 w-12 rounded-xl bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <UploadCloud className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-800">
                      {file ? "Click to replace file" : "Click to upload or drag & drop"}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Supports CSV, XLSX, TXT (max 5 MB)
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,.txt"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                {/* Selected file badge */}
                {file && (
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {(file.size / 1024).toFixed(1)} KB
                          {fileParsed && (
                            <span className="ml-2 text-blue-600 font-medium">
                              {fileParsed.awbNumbers.length + fileParsed.orderRefNumbers.length} entries found
                              {fileParsed.mode === "order_ref" ? " (Order Refs)" : " (AWBs)"}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={clearFile}
                      className="p-1.5 rounded-lg hover:bg-red-100 text-red-500 hover:text-red-600 transition-colors flex-shrink-0"
                      title="Remove file"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Helper info + sample download */}
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-start gap-2 text-xs text-slate-500 max-w-lg">
                    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                    <span>
                      File should have a column named <code className="px-1 py-0.5 bg-slate-100 rounded text-[0.7rem] font-mono">awb_number</code> or{" "}
                      <code className="px-1 py-0.5 bg-slate-100 rounded text-[0.7rem] font-mono">order_reference_number</code>.
                      If no header is found, the first column is used as AWB numbers.
                    </span>
                  </div>
                  <button
                    onClick={handleSampleDownload}
                    className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors flex-shrink-0"
                  >
                    <DownloadCloud className="w-3.5 h-3.5" />
                    Download Sample CSV
                  </button>
                </div>
              </div>
            )}

            {/* ─── MANUAL INPUT TAB ─── */}
            {inputMode === "manual" && (
              <div className="space-y-4">
                {/* AWB vs Order Ref toggle */}
                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg w-fit">
                  <button
                    onClick={() => setManualMode("awb")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      manualMode === "awb"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <ScanLine className="w-3.5 h-3.5" />
                    AWB Numbers
                  </button>
                  <button
                    onClick={() => setManualMode("order_ref")}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                      manualMode === "order_ref"
                        ? "bg-white text-blue-600 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    <Hash className="w-3.5 h-3.5" />
                    Order Reference Numbers
                  </button>
                </div>

                {/* Textarea */}
                <div className="relative">
                  <textarea
                    value={manualText}
                    onChange={(e) => { setManualText(e.target.value); setResults(null); }}
                    placeholder={
                      manualMode === "awb"
                        ? "Enter AWB numbers separated by commas or new lines:\n\n50845010161663\n50845010161664\n50845010161665"
                        : "Enter Order Reference numbers separated by commas or new lines:\n\nORD-001\nORD-002\nORD-003"
                    }
                    rows={6}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none resize-y font-mono"
                  />
                  {manualText && (
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[0.65rem] font-semibold">
                        {parseManualInput(manualText).length} entries
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex items-start gap-2 text-xs text-slate-500">
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" />
                  <span>
                    Separate multiple values with commas or new lines. Spaces around values are automatically trimmed.
                  </span>
                </div>
              </div>
            )}

            {/* ─── ACTION BUTTON ─── */}
            <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100 mt-4">
              <div className="text-xs text-slate-500">
                {entryCount > 0 && (
                  <span>
                    <span className="font-semibold text-slate-700">{entryCount}</span> {entryCount === 1 ? "entry" : "entries"} ready to process
                    {entryCount > 500 && (
                      <span className="text-red-500 ml-2">(max 500 allowed)</span>
                    )}
                  </span>
                )}
              </div>
              <button
                onClick={handleDownload}
                disabled={loading || entryCount === 0 || entryCount > 500}
                className={`inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-lg shadow-sm transition-all ${
                  loading || entryCount === 0 || entryCount > 500
                    ? "bg-blue-300 text-white cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-md active:scale-[0.98]"
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating Invoices...
                  </>
                ) : (
                  <>
                    <DownloadCloud className="w-4 h-4" />
                    Download Invoices
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ───── Loading state ───── */}
        {loading && (
          <div className="w-full rounded-xl border border-blue-200 bg-blue-50/50 shadow-sm p-6 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <div className="text-center">
              <p className="text-sm font-semibold text-slate-800">Processing your request...</p>
              <p className="text-xs text-slate-500 mt-1">
                Generating invoices for {entryCount} shipments. This may take a moment.
              </p>
            </div>
          </div>
        )}

        {/* ───── Results Section ───── */}
        {results && !loading && (
          <div className="space-y-4">

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Total */}
              <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{results.summary.total}</p>
                  <p className="text-xs text-slate-500">Total Processed</p>
                </div>
              </div>

              {/* Success */}
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 shadow-sm p-4 flex items-center gap-4">
                <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-700">{results.summary.success_count}</p>
                  <p className="text-xs text-emerald-600">Successful</p>
                </div>
              </div>

              {/* Failed */}
              <div className={`rounded-xl border shadow-sm p-4 flex items-center gap-4 ${
                results.summary.failed_count > 0
                  ? "border-red-200 bg-red-50/50"
                  : "border-slate-200 bg-white"
              }`}>
                <div className={`h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                  results.summary.failed_count > 0 ? "bg-red-100" : "bg-slate-100"
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    results.summary.failed_count > 0 ? "text-red-600" : "text-slate-400"
                  }`} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${
                    results.summary.failed_count > 0 ? "text-red-700" : "text-slate-400"
                  }`}>
                    {results.summary.failed_count}
                  </p>
                  <p className={`text-xs ${
                    results.summary.failed_count > 0 ? "text-red-600" : "text-slate-500"
                  }`}>
                    Failed
                  </p>
                </div>
              </div>
            </div>

            {/* Merged download banner */}
            {results.mergedUrl && (
              <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 shadow-sm p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">
                      Merged Invoice PDF Ready
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      All successful invoices combined into one PDF.
                      {results.expiresAt && (
                        <span className="ml-1">
                          Link expires {new Date(results.expiresAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}.
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => copyUrl(results.mergedUrl)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                    Copy Link
                  </button>
                  <button
                    onClick={() => window.open(results.mergedUrl, "_blank", "noopener,noreferrer")}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 shadow-sm transition-all"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open Merged PDF
                  </button>
                </div>
              </div>
            )}

            {/* Results table */}
            <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              {/* Table header bar */}
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-800">
                  Individual Results
                </h4>
                <div className="flex items-center gap-3 text-xs">
                  {failedItems.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">
                      <AlertTriangle className="w-3 h-3" />
                      {failedItems.length} failed
                    </span>
                  )}
                  {successItems.length > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                      <CheckCircle2 className="w-3 h-3" />
                      {successItems.length} succeeded
                    </span>
                  )}
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-[0.8rem] border-collapse">
                  <thead className="bg-slate-50 text-slate-600 uppercase text-[0.7rem] tracking-wide">
                    <tr>
                      <th className="p-3 text-left w-10">#</th>
                      <th className="p-3 text-left">
                        <div className="inline-flex items-center gap-1">
                          <ScanLine className="w-3 h-3" />
                          AWB Number
                        </div>
                      </th>
                      <th className="p-3 text-left">
                        <div className="inline-flex items-center gap-1">
                          <Hash className="w-3 h-3" />
                          Order Reference
                        </div>
                      </th>
                      <th className="p-3 text-left">Status</th>
                      <th className="p-3 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-slate-500 text-xs py-8">
                          No results to display.
                        </td>
                      </tr>
                    )}
                    {displayedResults.map((item, idx) => (
                      <tr
                        key={`${item.awb_number}-${idx}`}
                        className={`border-t border-slate-100 transition-colors ${
                          item.success
                            ? "hover:bg-slate-50/50"
                            : "bg-red-50/30 hover:bg-red-50/60"
                        }`}
                      >
                        <td className="p-3 text-slate-400 font-mono text-xs">
                          {idx + 1}
                        </td>
                        <td className="p-3">
                          <span className="font-medium text-slate-800 font-mono text-[0.78rem]">
                            {item.awb_number}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className="text-slate-700 text-[0.78rem]">
                            {item.order_reference_number}
                          </span>
                        </td>
                        <td className="p-3">
                          {item.success ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[0.7rem] font-medium">
                              <CheckCircle2 className="w-3 h-3" />
                              Success
                            </span>
                          ) : (
                            <div className="flex flex-col gap-0.5">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-700 text-[0.7rem] font-medium w-fit">
                                <XCircle className="w-3 h-3" />
                                Failed
                              </span>
                              {item.error_message && (
                                <span className="text-[0.65rem] text-red-500 mt-0.5 max-w-xs truncate" title={item.error_message}>
                                  {item.error_message}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-3">
                          {item.success && item.url ? (
                            <button
                              onClick={() => window.open(item.url, "_blank", "noopener,noreferrer")}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-[0.7rem] font-semibold hover:bg-blue-100 transition-colors"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View
                            </button>
                          ) : (
                            <span className="text-slate-400 text-[0.7rem]">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Show more / less */}
              {results.items.length > 10 && (
                <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-center">
                  <button
                    onClick={() => setShowAllResults(!showAllResults)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    {showAllResults ? (
                      <>
                        <ChevronUp className="w-3.5 h-3.5" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-3.5 h-3.5" />
                        Show all {results.items.length} results
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkInvoiceDownload;
